//lib
import { Icon } from "@iconify/react/dist/iconify.js";

//redux
import { useDispatch, useSelector } from "react-redux";

//component
import Col from "../../../components/element/col";
import Row from "../../../components/element/row";
import Button from "../../../components/element/button";
import ArrowButton from "../../../components/common/common.arrow.button";
import Container from "../../../components/element/container";
import ProductCarousel from "./product.carousel";
import ProductOption from "./product.option";
import Quantity from "../../../components/element/quantity";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import formatNumber from "../../../helpers/format.number";
import useWindowDimension from "../../../components/hook/use.window.dimension";

import { Form, Formik } from "formik";
import * as Yup from "yup";
import { useEffect } from "react";
import { showToast } from "../../../helpers/show.toast";
import { addCart, getCartInfo } from "../../../redux/reducer/cart.reducer";
import { handleCatchError } from "../../../helpers/handle.catch.error";
import { getCartCount } from "../../../redux/reducer/common.reducer";
import { Skeleton } from "primereact/skeleton";
import { openLoginModal } from "../../../redux/reducer/modal.reducer";

export default function ProductBasic({ product }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const { state } = useLocation();
  const { width } = useWindowDimension();
  const { addCartLoading } = useSelector((state) => state.cart);
  const { token, username } = useSelector((state) => state.common);
  const { productInfoLoading } = useSelector((state) => state.product);

  const handleNavigate = () => {
    navigate(state || "/");
  };

  const getVariantDetail = (selectedOptions, field = "id") => {
    if (
      product?.product_variants?.length > 0 &&
      Object.keys(selectedOptions)?.length > 0
    ) {
      for (const variant of product.product_variants) {
        let isMatch = true;
        if (variant.option_values?.length > 0) {
          for (const opt of variant.option_values) {
            const type = opt.option_type.name.toLowerCase();
            const value = opt.value.toLowerCase();
            if (
              !selectedOptions[type] ||
              selectedOptions[type].toLowerCase() !== value
            ) {
              isMatch = false;
              break;
            }
          }
        } else {
          isMatch = false;
        }
        if (isMatch) return variant[field];
      }
    }
    return null;
  };

  const initialVariant =
    product?.variants?.length > 0
      ? product.variants.reduce((acc, variant) => {
          acc[variant.name.toLowerCase()] = variant.options[0];
          return acc;
        }, {})
      : {};

  const productForm = {
    product_id: id,
    variant_id: "",
    // product?.variants?.length > 0 ? getVariantDetail(initialVariant) : "",
    options: {},
    quantity: 1,
  };

  const productSchema = Yup.object().shape({
    variant_id: Yup.string().required(),
  });

  const handleSubmit = (values) => {
    if (token && username) {
      dispatch(
        addCart({
          quantity: values.quantity,
          ...(values.variant_id
            ? { variant_id: values.variant_id }
            : { product_id: values.product_id }),
        })
      )
        .unwrap()
        .then(() => {
          if (values.buy_now) {
            navigate("/cart");
          }

          dispatch(getCartInfo());
          dispatch(getCartCount());

          showToast(
            "Added to Cart!",
            "The item has been added to your cart.",
            "success"
          );
        })
        .catch((ex) => {
          handleCatchError(ex, null);
        });
    } else {
      dispatch(openLoginModal());
    }
  };

  return (
    productInfoLoading !== undefined && (
      <Formik
        initialValues={productForm}
        validationSchema={product?.variants?.length > 0 ? productSchema : ""}
        enableReinitialize={true}
        onSubmit={(values, { setFieldError }) => {
          handleSubmit(values, setFieldError);
        }}
      >
        {({ values, setFieldValue, isValid }) => (
          <Form className="w-100">
            <Container
              className={`product-basic ${
                !productInfoLoading ? "--loading" : ""
              } relative`}
            >
              {width > 850 && (
                <ArrowButton
                  color="black"
                  onClick={() => handleNavigate("/")}
                />
              )}
              {productInfoLoading ? (
                <Col className="v-flex gap-4 overflow-hidden items-start">
                  <Skeleton
                    borderRadius="0"
                    width={
                      width < 750 ? "100%" : width < 991 ? "500px" : "100%"
                    }
                    height={width < 750 ? "400px" : "620px"}
                    className={width > 750 ? "p-galleria-item" : ""}
                  />
                  <Row className="gap-4 flex-nowrap w-100">
                    {Array.from({ length: 6 }, (_, productLoadingSkeleton) => (
                      <Skeleton
                        borderRadius="0"
                        className="p-galleria-thumbnail-item"
                        key={productLoadingSkeleton}
                      />
                    ))}
                  </Row>
                </Col>
              ) : (
                <ProductCarousel product={product} />
              )}
              {productInfoLoading ? (
                <Col className="_col product-info">
                  <Col className="v-flex gap-2">
                    <Skeleton
                      borderRadius="0"
                      width={width < 500 ? "280px" : "350px"}
                      height={width < 991 ? "24px" : "28px"}
                      className="_label product-title"
                    />
                    <Skeleton
                      borderRadius="0"
                      width="80px"
                      height={width < 991 ? "22px" : "25px"}
                      className="_label product-category"
                    />
                    <Skeleton
                      borderRadius="0"
                      width="150px"
                      height={width < 991 ? "28px" : "36px"}
                      className="_label product-price mt-3"
                    />
                    <hr className="my-4" />
                    {Array.from(
                      { length: width > 750 ? 2 : 1 },
                      (_, infoLoadingIndex) => (
                        <Col
                          className="v-flex gap-4 mb-4"
                          key={infoLoadingIndex}
                        >
                          <Skeleton
                            borderRadius="0"
                            width="110px"
                            height={width < 991 ? "18px" : "20px"}
                          />
                          <Row className="gap-3">
                            {Array.from(
                              { length: 2 },
                              (_, variantLoadingIndex) => (
                                <Skeleton
                                  borderRadius="0"
                                  width="120px"
                                  height={width < 991 ? "40px" : "42px"}
                                  key={variantLoadingIndex}
                                />
                              )
                            )}
                          </Row>
                        </Col>
                      )
                    )}
                  </Col>
                  <Col className="_col product-button">
                    <Row className="h-flex gap-4 mt-5 flex-nowrap">
                      {Array.from({ length: 2 }, (_, variantLoadingIndex) => (
                        <Skeleton
                          borderRadius="0"
                          width="100%"
                          height="48px"
                          key={variantLoadingIndex}
                        />
                      ))}
                    </Row>
                    <Skeleton borderRadius="0" width="100%" height="48px" />
                  </Col>
                </Col>
              ) : (
                <Col className="_col product-info">
                  <Col className="v-flex gap-2">
                    <h2 className="_label product-title">
                      {product.name?.name}
                    </h2>
                    <p className="_label product-category">
                      {product.category_model?.name}
                    </p>
                    <h1 className="_label product-price mt-3">
                      RM{" "}
                      {values.variant_id
                        ? formatNumber(
                            getVariantDetail(values.options, "price"),
                            "decimal"
                          )
                        : formatNumber(product.price, "range")}
                    </h1>
                    <hr className="my-4" />
                    <Col className="v-flex gap-4">
                      {!productInfoLoading &&
                        product.variants?.length > 0 &&
                        product.variants.map((variant, variantIndex) => (
                          <ProductOption
                            product={product}
                            optionList={variant.options}
                            key={variantIndex}
                            title={variant.name}
                            getVariantDetail={getVariantDetail}
                          />
                        ))}
                    </Col>
                  </Col>
                  <Col className="_col product-button">
                    <Row className="h-flex gap-4 mt-5 flex-nowrap">
                      <Quantity
                        name="quantity"
                        max={
                          product?.variants?.length > 0
                            ? getVariantDetail(values.options, "quantity")
                            : 0
                        }
                      />
                      <Button
                        type={
                          product?.variants?.length > 0 && !values.variant_id
                            ? "button"
                            : "submit"
                        }
                        disabled={
                          (addCartLoading ||
                            !isValid ||
                            product?.quantity === 0) &&
                          !(product?.variants?.length > 0 && !values.variant_id)
                        }
                        className={`_button add-cart w-100 ${
                          product?.variants?.length > 0 && !values.variant_id
                            ? "disabled pointer-event"
                            : ""
                        }`}
                        onClick={() => {
                          if (
                            product?.variants?.length > 0 &&
                            !values.variant_id
                          ) {
                            showToast(
                              "Almost there!",
                              "Please select all required options before adding to cart.",
                              "warning"
                            );
                          }
                        }}
                      >
                        Add to Cart
                      </Button>
                    </Row>
                    <Button
                      type={
                        product?.variants?.length > 0 && !values.variant_id
                          ? "button"
                          : "submit"
                      }
                      disabled={
                        (addCartLoading ||
                          !isValid ||
                          product?.quantity === 0) &&
                        !(product?.variants?.length > 0 && !values.variant_id)
                      }
                      className={`_button buy-now ${
                        product?.variants?.length > 0 && !values.variant_id
                          ? "disabled pointer-event"
                          : ""
                      }`}
                      onClick={() => {
                        if (
                          product?.variants?.length > 0 &&
                          !values.variant_id
                        ) {
                          showToast(
                            "Almost there!",
                            "Please select all required options before adding to cart.",
                            "error"
                          );
                        } else {
                          setFieldValue("buy_now", true);
                        }
                      }}
                    >
                      Buy Now
                    </Button>
                  </Col>
                </Col>
              )}
            </Container>
          </Form>
        )}
      </Formik>
    )
  );
}
